// src/gp.ts —— 代理端服务（TD-806 / TD-811）：API 客户端、会话、UI 原语；宿主在 app.didMount 时绑定
import { ApiClient, ApiError, type SessionLostDetail } from '../../shared/src/api/client';
import { isNotReady } from '../../shared/src/api/pending';
import type { DcHost, Dict, EventBus } from '../../shared/src/dc';
import { errorText, interpolate } from '../../shared/src/errors';
import { bootIdleExpired, browserIdleEnv, clearIdleMarks, createIdleMonitor, disposeIdleEnv, normalizeIdleMinutes, type IdleEnv, type IdleLogoutReason, type IdleMonitor } from '../../shared/src/idle';
import { TOKEN_KEYS, localTokenStore, type TokenStore } from '../../shared/src/storage';
import type { AgentState, FcpDraft } from './state';
import type { AgentMe } from './types';

export type ErrVars = Readonly<Record<string, string | number | undefined>>;

export interface AgentUi {
  toast(msg: string): void;
  copy(text: string): void;
  run<T>(fn: () => Promise<T>): Promise<T | undefined>;
  err(e: unknown, vars?: ErrVars): string;
}

// session:lost 原因：expired（refresh 失败，外壳切登录页）/ logout（主动退出）/ pwd_changed（改密吊销全部会话或被要求强制改密；
// 外壳不按 expired 处理切屏，各域照常复位切片）/ idle_timeout（空闲到期：前端判定或服务端 401 reason）/ max_age（超过最长在线，服务端 401 reason）
export type SessionLostReason = 'expired' | 'logout' | 'pwd_changed' | 'idle_timeout' | 'max_age';
// 外壳据此切登录页的原因（其余原因各域只复位切片）
export const LOGIN_REASONS: readonly string[] = ['expired', 'idle_timeout', 'max_age'];

export interface AgentSession {
  established(token: string, opts: { persist: boolean; restricted: boolean; reason: 'boot' | 'login' }): Promise<void>;
  reloadMe(): Promise<void>;
  logout(): Promise<void>;
  // 改密成功（A-01b / A-08，S-97）：后端已吊销本人全部会话 → 清 token、回登录页、toast 重新登录
  passwordChanged(): void;
  // 会话代：单调递增。各域发业务请求前记下 epoch，写入前不一致即丢弃（跨账号不串数据）；
  // 同会话内 401 → refresh 换发 access token 不换代（不按 token 比较）
  epoch(): number;
  // 启动期空闲判定（2026-10-15）：上次会话关页期间已超过空闲阈值 → 吊销 / 清 token，返回 true（调用方直接落登录页并提示）
  bootIdle(): Promise<boolean>;
  // 启动期 refresh 带原因失败（空闲 / 超过最长在线）的登录页提示文案；无原因为空串
  bootNotice(): string;
  // 空闲提示框「继续使用」：refresh 续期并重置计时
  continueIdle(): Promise<void>;
  // 宿主卸载：停空闲计时、关闭跨标签页通道
  dispose(): void;
}

export interface AgentGp {
  api: ApiClient;
  tokens: TokenStore;
  events: EventBus;
  ui: AgentUi;
  session: AgentSession;
  bind(host: DcHost<AgentState>): void;
  host(): DcHost<AgentState>;
}

export const TOAST_MS = 2200;
// GET /api/agent/me 缺 session 字段（旧后端）时的空闲阈值，与后端 agentIdleMinutes 默认值一致
export const AGENT_IDLE_DEFAULT_MINUTES = 120;

function dictText(d: Dict, k: string): string {
  const v = d[k];
  return typeof v === 'string' ? v : '';
}

// 会话终止提示（登录页 loginErr 区显示，直到用户开始输入）：空闲退出 / 超过最长在线 / 通用过期
export function sessionNotice(d: Dict, reason: string, detail?: SessionLostDetail): string {
  if (reason === 'idle_timeout') return dictText(d, 'idleLoggedOut');
  if (reason === 'max_age') {
    const n = detail?.maxHours;
    return typeof n === 'number' && n > 0 ? interpolate(dictText(d, 'sessionMaxAge'), { n }) : dictText(d, 'sessionExpired');
  }
  return '';
}

export function emptyFcp(): FcpDraft {
  return { old: '', nw: '', nw2: '', err: '' };
}

// idleEnv 可注入：缺省取浏览器空闲环境（node 用例无 document → null，不启用）；测试注入假环境
export function createServices(events: EventBus, dict: () => Dict, apiBase: string, idleEnvIn?: IdleEnv | null): AgentGp {
  let current: DcHost<AgentState> | null = null;
  let epoch = 0;
  const host = (): DcHost<AgentState> => {
    if (!current) throw new Error('代理端宿主尚未绑定（didMount 之前调用了服务）');
    return current;
  };
  // 会话结束统一出口：先换代再广播，监听器内读到的 epoch 已是新代（在途结果随即失效）
  const endSession = (reason: SessionLostReason): void => {
    epoch += 1;
    events.emit('session:lost', { reason });
  };
  // TD-811：persist=true 写 localStorage.gpu_at_agent；受限窗口 token（persist=false）只存内存
  const tokens = localTokenStore(TOKEN_KEYS.agent);
  const idleEnv = idleEnvIn === undefined ? browserIdleEnv('agent') : idleEnvIn;
  let idle: IdleMonitor | null = null; // 进壳（session:ready）后运行；其余时刻停止
  // 登录页提示（外壳 session:lost 监听器已切登录页之后写入，避免被其清空）
  const showNotice = (notice: string): void => {
    const h = current;
    if (!h || !notice) return;
    h.setState({ sh: { ...h.state.sh, loginErr: notice } });
  };
  const closeIdle = (): void => {
    const h = current;
    if (h && h.state.sh.idleLeft !== 0) h.setState({ sh: { ...h.state.sh, idleLeft: 0 } });
  };
  const api = new ApiClient({
    base: apiBase,
    aud: 'agent',
    tokens,
    onSessionLost: (reason, detail) => {
      // 进壳后的会话失效：其他标签页一起本地退出（boot 期 / 受限窗口未运行计时，不广播）
      idle?.stop(idle.running ? 'session_lost' : undefined);
      closeIdle();
      const r: SessionLostReason = reason === 'idle_timeout' || reason === 'max_age' ? reason : 'expired';
      endSession(r);
      showNotice(sessionNotice(dict(), r, detail));
    },
    onAuthedRequest: () => idle?.noteRequest(),
    log: (event, detail) => console.warn('[gp:api]', event, detail),
  });
  // 本地结束会话（不再请求服务端）：空闲到期 / 其他标签页已退出
  const endLocal = (reason: SessionLostReason, notice: string): void => {
    tokens.clear();
    const h = current;
    if (h) h.setState({ screen: 'login', modal: null, sh: { ...h.state.sh, me: null, fcp: null, loginPwd: '', loginErr: notice, idleLeft: 0 } });
    endSession(reason);
  };
  const ui: AgentUi = {
    toast(msg) {
      if (!msg) return;
      const h = host();
      const id = h.state.toastId + 1;
      h.setState({ toast: msg, toastId: id });
      setTimeout(() => {
        if (h.state.toastId === id) h.setState({ toast: '' });
      }, TOAST_MS);
    },
    copy(text) {
      try {
        // 06 A-02：clipboard 不可用或拒绝时静默，toast 照常
        navigator.clipboard?.writeText(text).catch((e: unknown) => console.warn('[gp:agent] clipboard 写入失败', e));
      } catch (e) {
        console.warn('[gp:agent] clipboard 不可用', e);
      }
      ui.toast(String(dict().toastCopied ?? ''));
    },
    async run(fn) {
      const h = host();
      if (h.state.saving) return undefined;
      h.setState({ saving: true });
      try {
        return await fn();
      } finally {
        h.setState({ saving: false });
      }
    },
    err(e, vars) {
      const d = dict();
      if (e instanceof ApiError) {
        // 06 A-00 / A-01b：受限会话下收到 1501 即路由回首登改密页
        const h = host();
        if (e.code === 1501 && h.state.sh.fcp) h.setState({ screen: 'fcp', modal: null });
        return errorText('agent', e, d, vars);
      }
      if (isNotReady(e)) return String(d.empty ?? '');
      console.error('[gp:agent]', e);
      return String(d.toastNetwork ?? '');
    },
  };
  if (idleEnv) {
    idle = createIdleMonitor('agent', idleEnv, {
      warn: (n) => {
        const h = current;
        if (h && h.state.sh.idleLeft !== n) h.setState({ sh: { ...h.state.sh, idleLeft: n } });
      },
      unwarn: closeIdle,
      // 本标签页空闲到期（监视器已广播退出）：先请服务端吊销，失败也本地退出
      expire: () => {
        void (async () => {
          const my = epoch;
          try {
            if (tokens.get()) await api.call('POST /api/agent/logout');
          } catch (e) {
            console.warn('[gp:agent] 空闲退出 logout 请求失败，本地照常退出', e);
          }
          // logout 在途期间已因 401 结束会话（onSessionLost 已换代并提示）→ 不重复
          if (my !== epoch) return;
          endLocal('idle_timeout', sessionNotice(dict(), 'idle_timeout'));
        })();
      },
      remoteLogout: (reason: IdleLogoutReason) => endLocal(reason === 'idle_timeout' ? 'idle_timeout' : 'logout', reason === 'idle_timeout' ? sessionNotice(dict(), 'idle_timeout') : ''),
      keepAlive: () => {
        session.reloadMe().catch((e: unknown) => console.warn('[gp:agent] 保活 /me 失败', e));
      },
      renew: () => api.renewSession(),
    });
  }

  const session: AgentSession = {
    async established(token, opts) {
      const h = host();
      tokens.set(token, opts.persist);
      if (opts.restricted) {
        // 受限窗口（S-72）：只允许 /me 与 PUT /password，直接进首登改密页，不拉业务数据
        h.setState({ screen: 'fcp', modal: null, sh: { ...h.state.sh, me: null, fcp: emptyFcp() } });
        return;
      }
      const me = await api.call<AgentMe>('GET /api/agent/me');
      if (me.must_change_password) {
        h.setState({ screen: 'fcp', modal: null, sh: { ...h.state.sh, me, fcp: emptyFcp() } });
        return;
      }
      // 先换代再进壳：setState 触发的域 update / 渲染期请求记下的已是新代，不会被随后的换代误判为上一会话
      epoch += 1;
      h.setState({ screen: 'app', page: 'home', modal: null, sh: { ...h.state.sh, me, fcp: null, idleLeft: 0, loginErr: '' } });
      events.emit('session:ready', { reason: opts.reason });
      idle?.start(normalizeIdleMinutes(me.session?.idle_minutes, AGENT_IDLE_DEFAULT_MINUTES));
    },
    async reloadMe() {
      const h = host();
      const me = await api.call<AgentMe>('GET /api/agent/me');
      // 06 A-02 全局框架：页面切换时刷新 /me；must_change_password=1 路由回 A-01b（进入受限窗口：各域复位切片、丢弃在途结果）
      if (me.must_change_password) {
        const entering = h.state.screen !== 'fcp';
        idle?.stop(); // 进入受限窗口：不再计时
        h.setState({ screen: 'fcp', modal: null, sh: { ...h.state.sh, me, fcp: h.state.sh.fcp ?? emptyFcp(), idleLeft: 0 } });
        if (entering) endSession('pwd_changed');
      } else {
        h.setState({ sh: { ...h.state.sh, me } });
        // 阈值可能已在系统设置中调整：运行中只更新阈值、不重置最后活动
        if (idle?.running) idle.start(normalizeIdleMinutes(me.session?.idle_minutes, AGENT_IDLE_DEFAULT_MINUTES));
      }
    },
    async logout() {
      idle?.stop('logout'); // 其他标签页一起本地退出
      try {
        await api.call('POST /api/agent/logout');
      } catch (e) {
        console.warn('[gp:agent] logout 请求失败，本地照常退出', e);
      }
      tokens.clear();
      const h = host();
      h.setState({ screen: 'login', modal: null, sh: { ...h.state.sh, me: null, fcp: null, loginPwd: '', loginErr: '', idleLeft: 0 } });
      endSession('logout');
    },
    passwordChanged() {
      idle?.stop('session_lost'); // 后端已吊销本人全部会话：其他标签页一起本地退出
      tokens.clear();
      const h = host();
      h.setState({ screen: 'login', modal: null, sh: { ...h.state.sh, me: null, fcp: null, loginPwd: '', loginErr: '', idleLeft: 0 } });
      endSession('pwd_changed');
      ui.toast(String(dict().toastPwdRelogin ?? ''));
    },
    epoch: () => epoch,
    async bootIdle() {
      if (!idleEnv || !bootIdleExpired(idleEnv.store, 'agent', idleEnv.now())) return false;
      try {
        if (tokens.get()) await api.call('POST /api/agent/logout');
      } catch (e) {
        console.warn('[gp:agent] 空闲退出 logout 请求失败，本地照常退出', e);
      }
      tokens.clear();
      clearIdleMarks(idleEnv.store, 'agent');
      showNotice(sessionNotice(dict(), 'idle_timeout'));
      return true;
    },
    bootNotice() {
      const loss = api.lastLoss;
      return loss ? sessionNotice(dict(), loss.reason, loss.detail) : '';
    },
    async continueIdle() {
      const ok = (await idle?.continueSession()) ?? false;
      // 续期失败由 ApiClient 回调 onSessionLost（带原因 / expired）完成退出与提示；这里只兜底关闭提示框
      if (!ok) closeIdle();
    },
    dispose() {
      idle?.dispose();
      disposeIdleEnv(idleEnv);
    },
  };
  return {
    api,
    tokens,
    events,
    ui,
    session,
    bind(h) {
      current = h;
    },
    host,
  };
}
